<template>
  <div class="chatpdf-test py-4 px-3 w-100">
<br>
<br>
<br>
<br>
<br>
<br>
<br>
    <!-- ===================== LocalChat AI ===================== -->
    <div class="card shadow-sm border-0 mb-4 lc-card">
      <div
        class="card-header lc-card__header d-flex align-items-center justify-content-between"
        style="cursor:pointer"
        @click="lcOpen = !lcOpen"
      >
        <span class="font-weight-bold">
          <i class="bi bi-robot mr-2"></i>LocalChat AI
          <span
            v-if="lcProviderLabel"
            class="badge badge-pill badge-light ml-2 small font-weight-normal text-muted border"
          >{{ lcProviderLabel }}</span>
        </span>
        <span>
          <span v-if="lcMessages.length" class="badge badge-secondary badge-pill mr-2 small">{{ lcMessages.length }} msg</span>
          <i class="bi" :class="lcOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </span>
      </div>

      <div v-show="lcOpen" class="card-body p-0">
        <!-- Token input -->
        <div class="px-3 pt-3 pb-2 border-bottom bg-light">
          <div class="d-flex align-items-center flex-wrap" style="gap:8px">
            <div class="flex-grow-1" style="min-width:200px">
              <input
                v-model.trim="lcToken"
                type="password"
                class="form-control form-control-sm font-monospace"
                placeholder="Bearer token (défaut: lc_key)"
                autocomplete="off"
                @change="lcFetchProvider"
              />
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="lcFetchProvider"
              title="Vérifier le provider actif"
            >
              <i class="bi bi-arrow-clockwise"></i>
            </button>
            <button
              v-if="lcMessages.length"
              type="button"
              class="btn btn-sm btn-outline-danger"
              @click="lcClear"
            >
              <i class="bi bi-trash mr-1"></i>Effacer
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div ref="lcMessages" class="lc-messages px-3 py-3">
          <div
            v-if="!lcMessages.length"
            class="text-center text-muted small py-4"
          >
            <i class="bi bi-chat-dots" style="font-size:2rem;opacity:.3"></i>
            <p class="mt-2 mb-0">Démarrez la conversation…</p>
            <p class="small mt-1 mb-0 text-muted" style="font-size:.75rem">
              Texte seul → <code>/api/ask</code> (historique) &nbsp;|&nbsp;
              Avec fichier → <code>/v1/chat</code> (multipart)
            </p>
          </div>

          <div
            v-for="(msg, idx) in lcMessages"
            :key="idx"
            class="lc-msg d-flex mb-3"
            :class="msg.role === 'user' ? 'lc-msg--user' : 'lc-msg--assistant'"
          >
            <div class="lc-msg__avatar mr-2 flex-shrink-0">
              <i
                class="bi"
                :class="msg.role === 'user' ? 'bi-person-fill' : 'bi-robot'"
              ></i>
            </div>
            <div class="lc-msg__bubble flex-grow-1">
              <div v-if="msg.fileName" class="lc-file-badge mb-1">
                <i class="bi bi-paperclip mr-1"></i>{{ msg.fileName }}
              </div>
              <pre class="lc-msg__text mb-0">{{ msg.content }}</pre>
              <div v-if="msg.role === 'assistant' && msg.streaming" class="lc-typing mt-1">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-if="lcError" class="px-3 pb-2">
          <div class="alert alert-danger small py-2 mb-0">
            <i class="bi bi-exclamation-triangle mr-1"></i>{{ lcError }}
          </div>
        </div>

        <!-- Attached file preview -->
        <div v-if="lcFile" class="px-3 pb-1">
          <span class="badge badge-info d-inline-flex align-items-center" style="font-size:.8rem;gap:4px">
            <i class="bi bi-paperclip"></i>
            {{ lcFile.name }}
            <button
              type="button"
              class="btn btn-link p-0 ml-1 text-white lc-remove-file"
              @click="lcFile = null"
              title="Retirer le fichier"
            >&times;</button>
          </span>
          <small class="text-muted ml-2">→ envoyé via <code>/v1/chat</code></small>
        </div>

        <!-- Input area -->
        <div class="lc-input-area px-3 py-3 border-top">
          <input
            ref="lcFileInput"
            type="file"
            class="d-none"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.bmp,.tiff,.tif,.txt"
            @change="lcOnFileSelected"
          />
          <div class="d-flex align-items-end" style="gap:8px">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm flex-shrink-0 lc-attach-btn"
              :disabled="lcStreaming"
              @click="$refs.lcFileInput.click()"
              title="Joindre un fichier (PDF, image, texte)"
            >
              <i class="bi bi-paperclip"></i>
            </button>
            <textarea
              v-model="lcInput"
              class="form-control lc-textarea"
              rows="2"
              placeholder="Votre message… (Ctrl+Entrée pour envoyer)"
              :disabled="lcStreaming"
              @keydown.ctrl.enter.prevent="sendLocalChat"
            ></textarea>
            <button
              type="button"
              class="btn btn-primary lc-send-btn flex-shrink-0"
              :disabled="(!lcInput.trim() && !lcFile) || lcStreaming"
              @click="sendLocalChat"
            >
              <span
                v-if="lcStreaming"
                class="spinner-border spinner-border-sm"
                role="status"
              ></span>
              <i v-else class="bi bi-send-fill"></i>
            </button>
          </div>
          <small class="text-muted d-block mt-1">Ctrl+Entrée pour envoyer</small>
        </div>
      </div>
    </div>
    <!-- ============================================================ -->

    <div class="chatpdf-test__inner w-100">
      <header class="mb-4">
        <h1 class="h4 mb-1">Analyse de fichier — IA locale</h1>
        <p class="text-muted small mb-0">
          Choisissez un fichier (PDF, image, Excel, Word…) pour l’analyser via
          <code>http://127.0.0.1:7860/api/ask</code>.
        </p>
      </header>

      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <label class="font-weight-bold d-block mb-2" for="chatpdf-file"
            >Fichier</label
          >
          <input
            id="chatpdf-file"
            ref="fileInput"
            type="file"
            class="form-control"
            :accept="IMPORT_FILE_ACCEPT"
            :disabled="uploading"
            @change="onFileSelected"
          />
          <small class="text-muted d-block mt-1">
            PDF, image, Excel, CSV, Word… Le fichier est envoyé au serveur IA local pour analyse.
          </small>
          <div
            v-if="uploadedFileName"
            class="mt-2 d-flex align-items-center flex-wrap"
          >
            <span
              class="d-inline-block border border-success rounded px-2 py-1 text-success small mr-2 mb-1"
            >
              <i
                class="bi"
                :class="lastUploadedIsPdf ? 'bi-file-earmark-pdf' : 'bi-file-earmark'"
              ></i>
              {{ uploadedFileName }}
            </span>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-1"
              :disabled="uploading || chatting"
              @click="resetFile"
            >
              Changer de fichier
            </button>
          </div>
          <div v-if="uploading" class="mt-2 text-primary small">
            <span
              class="spinner-border spinner-border-sm mr-2"
              role="status"
            ></span>
            {{ uploadingLabel }}
          </div>
        </div>
      </div>

      <div
        v-if="importInfoMessage"
        class="alert alert-info small mb-3"
        role="status"
      >
        {{ importInfoMessage }}
      </div>

      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <label class="font-weight-bold d-block mb-2" for="chatpdf-question"
            >Question</label
          >
          <textarea
            id="chatpdf-question"
            v-model.trim="question"
            class="form-control"
            rows="3"
            placeholder="Ex. : Résume ce document en 3 points."
            :disabled="!canChat || chatting"
          ></textarea>
          <div class="d-flex justify-content-end mt-3">
            <button
              type="button"
              class="btn btn-primary px-4"
              :disabled="!canSend || chatting"
              @click="sendQuestion"
            >
              <span
                v-if="chatting"
                class="spinner-border spinner-border-sm mr-2"
                role="status"
              ></span>
              {{ chatting ? "Envoi…" : "Send" }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger mb-3"
        role="alert"
      >
        <strong>Erreur.</strong>
        {{ errorMessage }}
      </div>

      <div v-if="lastResponse" class="card shadow-sm border-0 chatpdf-test__answer">
        <div class="card-header border-0 py-2 chatpdf-test__answer-head">
          <span class="font-weight-bold small text-muted">Réponse</span>
        </div>
        <div class="card-body">
          <pre class="chatpdf-test__pre mb-0">{{ lastResponse }}</pre>
          <div class="border-top pt-3 mt-3">
            <p class="small text-muted mb-2">
              Si la réponse contient un <strong>tableau JSON</strong> de lignes facture
              (même format que l’import DUM), vous pouvez les enregistrer en
              <strong>lignes dossier</strong> (<code class="small">POST …/consignments_items</code>),
              en plus du catalogue <code class="small">items</code> si besoin.
            </p>
            <button
              type="button"
              class="btn btn-sm btn-outline-success"
              :disabled="importFromResponseRunning || !lastResponse"
              @click="importLastResponseLinesToConsignment"
            >
              <span
                v-if="importFromResponseRunning"
                class="spinner-border spinner-border-sm mr-1"
                role="status"
              ></span>
              {{
                importFromResponseRunning
                  ? "Import…"
                  : "Ajouter ces lignes aux articles du dossier"
              }}
            </button>
            <p
              v-if="importFromResponseError"
              class="text-danger small mt-2 mb-0"
            >
              {{ importFromResponseError }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Consignment Create page (embedded) -->
    <div class="mt-4 chatpdf-test__section-wide w-100 px-0 px-sm-1">
      <!-- Carte seule : fichier (PDF → ChatPDF ; autres → pièce jointe) -->
      <div class="card border-primary shadow-sm mb-3">
        <div class="card-header bg-light py-2">
          <span class="font-weight-bold small">Fichier (PDF ou autre)</span>
        </div>
        <div class="card-body">
          <p class="small text-muted mb-3">
            Choisissez un fichier (PDF, image, Excel…) — il sera envoyé au serveur IA local
            <code>http://127.0.0.1:7860/api/ask</code> pour extraction automatique.
          </p>
          <input
            ref="dumSectionFileInput"
            type="file"
            class="d-none"
            :accept="IMPORT_FILE_ACCEPT"
            :disabled="uploading"
            @change="onDumSectionFileSelected"
          />
          <div class="d-flex flex-wrap align-items-center">
            <button
              type="button"
              class="btn btn-primary mr-2 mb-1"
              :disabled="uploading"
              @click="triggerDumSectionFilePick"
            >
              <i class="bi bi-folder2-open"></i>
              Choisir un fichier
            </button>
            <button
              v-if="uploadedFileName"
              type="button"
              class="btn btn-outline-secondary btn-sm mb-1"
              :disabled="uploading"
              @click="resetFile"
            >
              Réinitialiser
            </button>
          </div>
          <div
            v-if="uploadedFileName"
            class="mt-3 d-flex align-items-center flex-wrap"
          >
            <span
              class="d-inline-block border border-success rounded px-2 py-1 text-success small mr-2 mb-1"
            >
              <i
                class="bi"
                :class="
                  lastUploadedIsPdf ? 'bi-file-earmark-pdf' : 'bi-file-earmark'
                "
              ></i>
              {{ uploadedFileName }}
            </span>
          </div>
          <div v-if="uploading" class="mt-2 text-primary small">
            <span
              class="spinner-border spinner-border-sm mr-2"
              role="status"
            ></span>
            {{ uploadingLabel }}
          </div>
          <div
            v-if="errorMessage"
            class="alert alert-danger small mt-3 mb-0 py-2"
            role="alert"
          >
            <strong>Erreur.</strong> {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="card border-secondary shadow-sm mb-3">
        <div class="card-body py-3">
          <h2 class="h6 mb-2">Facture fournisseur / DUM — import de fichier</h2>
          <p class="small text-muted mb-2">
            <strong>Étape 1 :</strong> choisissez le fichier (PDF, image, Excel…) avec la carte
            <strong>Fichier</strong> ci-dessus. Le fichier sera lu localement et envoyé au
            serveur IA local lors de l’analyse.
          </p>
          <p class="small text-muted mb-2 mb-md-3">
            <strong>Étape 2 :</strong> cliquez sur
            <strong>Remplir formulaire</strong> pour extraire les lignes via
            <code class="small">http://127.0.0.1:7860/api/ask</code>. Chaque
            ligne valide est enregistrée via l’API
            <code class="small">consignments_items</code> (tableau « Articles du dossier ») ;
            les articles catalogue sont créés ou réutilisés (<code class="small">items</code>)
            selon la désignation.
          </p>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="scrollToDumSection"
            >
              Aller au bloc DUM / fournisseur
            </button>
            <button
              type="button"
              class="btn btn-sm btn-success"
              :disabled="!canFillDumFromPdf || fillDumRunning"
              @click="fillDumFormFromChatPdf"
            >
              <span
                v-if="fillDumRunning"
                class="spinner-border spinner-border-sm mr-1"
                role="status"
              ></span>
              {{
                fillDumRunning
                  ? "Analyse en cours…"
                  : "Remplir formulaire (IA locale)"
              }}
            </button>
          </div>
          <p v-if="fillDumError" class="text-danger small mt-2 mb-0">
            {{ fillDumError }}
          </p>
        </div>
      </div>
      <consignment-create ref="consignmentCreateRef" />
    </div>
  </div>
</template>

<script>
import ConsignmentCreate from "./consignments/Create.vue";
import constant from "@/constant";

const LOCAL_CHAT_BASE = constant.localChatApi || "http://127.0.0.1:7860";

/** Fichiers sélectionnables ; ChatPDF n’analyse que les PDF. */
const IMPORT_FILE_ACCEPT = "*/*";

const FULL_IMPORT_PROMPT = `Tu analyses un fichier qui peut contenir PLUSIEURS documents (ex: facture fournisseur + 1 ou plusieurs autres factures/avoirs/transport).

Objectif: retourner un SEUL objet JSON (pas de markdown) contenant:

1) supplier_invoice: l'en-tête de la facture FOURNISSEUR PRINCIPALE (celle qui contient les marchandises). Si introuvable, mets null.
- supplier_fullName (string)
- invoice_number (string)
- invoice_date (YYYY-MM-DD ou "")
- currency: "MAD" | "EURO" | "USD"
- exchange_rate: number (taux vers MAD). IMPORTANT: si currency="MAD" alors exchange_rate=1. Si currency="USD" ou "EURO" et un taux est indiqué sur le document, renvoie sa valeur numérique (ex: 10.25). Ne renvoie jamais un texte.

2) other_invoices: tableau des autres factures/coûts trouvés dans le fichier (transport, assurance, transit, etc.), même si le fournisseur n'est pas lisible:
- invoice_number (string) ou ""
- invoice_date (YYYY-MM-DD ou "")
- supplier_fullName (string) ou "" (peut être vide)
- currency: "MAD" | "EURO" | "USD"
- exchange_rate: number (mêmes règles que ci-dessus; si absent sur l'une des factures, mets null)
- total_ht: number >= 0 ou null
- total_ttc: number >= 0 ou null
- total_tva: number >= 0 ou null
- tva: number >= 0 ou null
- remark: string

3) items: tableau des lignes marchandises (une entrée JSON par ligne de facture fournisseur principale seulement):
- designation (string)
- measuring: normaliser en M3, M2, Qx, Kg, PCS, M ou "" si absent
- quantity (number > 0) dans l'unité measuring
- unit_price_ht (number >= 0) ou null
- total_ht (number >= 0) ou null
- quantity_pcs (number) ou null
- pcs (number) ou null

Règles:
- Ne mets aucun texte hors JSON.
- Si une valeur est introuvable, mets "" ou null selon le champ.

Format:
{"supplier_invoice":null,"other_invoices":[],"items":[]}`;

function extractJsonValueFromText(text) {
  if (!text || typeof text !== "string") return null;
  const trimmed = text.trim();
  const objStart = trimmed.indexOf("{");
  const objEnd = trimmed.lastIndexOf("}");
  if (objStart !== -1 && objEnd !== -1 && objEnd > objStart) {
    try {
      return JSON.parse(trimmed.slice(objStart, objEnd + 1));
    } catch (e) {
      // ignore
    }
  }
  const arrStart = trimmed.indexOf("[");
  const arrEnd = trimmed.lastIndexOf("]");
  if (arrStart !== -1 && arrEnd !== -1 && arrEnd > arrStart) {
    try {
      return JSON.parse(trimmed.slice(arrStart, arrEnd + 1));
    } catch (e) {
      // ignore
    }
  }
  return null;
}

export default {
  name: "ChatPdfTestView",
  components: {
    ConsignmentCreate,
  },
  data() {
    return {
      // ---- LocalChat AI ----
      lcOpen: true,
      lcToken: "lc_adi7_nNp6PD_8V0VtS601CkJLRYv_2Q3JGmMePKqi_FwmrrR",
      lcMessages: [],
      lcInput: "",
      lcStreaming: false,
      lcError: "",
      lcProviderLabel: "",
      lcFile: null,

      // ---- File / Local AI ----
      uploadedFileName: "",
      fileBase64: "",
      fileMimeType: "",
      question: "",
      lastResponse: "",
      errorMessage: "",
      uploading: false,
      chatting: false,
      IMPORT_FILE_ACCEPT,
      lastUploadedImportFile: null,
      fillDumRunning: false,
      fillDumError: "",
      importFromResponseRunning: false,
      importFromResponseError: "",
    };
  },
  computed: {
    canChat() {
      return Boolean(this.fileBase64 && !this.uploading);
    },
    canSend() {
      return this.canChat && this.question.length > 0;
    },
    canFillDumFromPdf() {
      return Boolean(this.fileBase64 && !this.uploading);
    },
    lastUploadedIsPdf() {
      const f = this.lastUploadedImportFile;
      if (!f || !f.name) return false;
      return (
        f.type === "application/pdf" ||
        String(f.name).toLowerCase().endsWith(".pdf")
      );
    },
    importInfoMessage() {
      return "";
    },
    uploadingLabel() {
      return "Lecture du fichier…";
    },
  },
  mounted() {
    this.$store.dispatch("consignment/getAllItems");
    this.$store.dispatch("consignment/getAllCosts");
    this.lcFetchProvider();
  },
  methods: {
    parseExchangeRate(value) {
      if (value == null) return null;
      if (typeof value === "number") {
        return isFinite(value) && value > 0 ? value : null;
      }
      const s = String(value).trim().replace(",", ".");
      const m = s.match(/-?\d+(\.\d+)?/);
      if (!m) return null;
      const n = parseFloat(m[0]);
      return isFinite(n) && n > 0 ? n : null;
    },
    normalizeCurrency(value) {
      const c = String(value || "MAD").trim().toUpperCase();
      if (c === "EUR") return "EURO";
      if (!["MAD", "EURO", "USD"].includes(c)) return "MAD";
      return c;
    },
    normalizeItemName(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    },
    groupSupplierItemsByFullName(items) {
      if (!Array.isArray(items) || items.length === 0) return [];
      const map = new Map();

      for (const raw of items) {
        if (!raw || typeof raw !== "object") continue;
        const designation = String(raw.designation || "").trim();
        if (!designation) continue;
        const key = this.normalizeItemName(designation);

        const qty = parseFloat(raw.quantity);
        const safeQty = isFinite(qty) && qty > 0 ? qty : 0;

        // total_ht preferred; else unit_price_ht * qty
        const th = parseFloat(raw.total_ht);
        const up = parseFloat(raw.unit_price_ht);
        const safeTotal =
          isFinite(th) && th >= 0
            ? th
            : isFinite(up) && up >= 0 && safeQty > 0
              ? up * safeQty
              : 0;

        const prev = map.get(key);
        if (!prev) {
          map.set(key, {
            ...raw,
            designation,
            quantity: safeQty,
            total_ht: safeTotal,
            // once grouped, unit price may be ambiguous
            unit_price_ht: null,
          });
        } else {
          prev.quantity = (parseFloat(prev.quantity) || 0) + safeQty;
          prev.total_ht = (parseFloat(prev.total_ht) || 0) + safeTotal;
          prev.unit_price_ht = null;
          if (!prev.measuring && raw.measuring) prev.measuring = raw.measuring;

          const qpPrev = parseFloat(prev.quantity_pcs);
          const qpRaw = parseFloat(raw.quantity_pcs);
          if (isFinite(qpRaw) && qpRaw > 0) {
            prev.quantity_pcs = (isFinite(qpPrev) ? qpPrev : 0) + qpRaw;
          }
          if ((prev.pcs == null || prev.pcs === "") && raw.pcs != null && raw.pcs !== "") {
            prev.pcs = raw.pcs;
          }
        }
      }

      return Array.from(map.values());
    },
    computeItemsTotalInDocCurrency(items) {
      if (!Array.isArray(items)) return 0;
      let sum = 0;
      for (const raw of items) {
        if (!raw || typeof raw !== "object") continue;
        const th = parseFloat(raw.total_ht);
        if (isFinite(th) && th >= 0) {
          sum += th;
          continue;
        }
        const q = parseFloat(raw.quantity);
        const up = parseFloat(raw.unit_price_ht);
        if (isFinite(q) && q > 0 && isFinite(up) && up >= 0) {
          sum += q * up;
        }
      }
      return sum;
    },
    nearlyEqualTotals(a, b) {
      const x = parseFloat(a);
      const y = parseFloat(b);
      if (!isFinite(x) || !isFinite(y)) return true; // can't validate
      const diff = Math.abs(x - y);
      const tol = Math.max(1, 0.01 * Math.max(Math.abs(x), Math.abs(y))); // 1 currency unit or 1%
      return diff <= tol;
    },
    async fillDumFormFromChatPdf() {
      if (!this.canFillDumFromPdf || this.fillDumRunning) return;
      this.fillDumRunning = true;
      this.fillDumError = "";

      try {
        const response = await this.$api.instance.post("chatbot/localchat", {
          message:   FULL_IMPORT_PROMPT,
          file_data: this.fileBase64,
          file_name: this.uploadedFileName,
          mime_type: this.fileMimeType,
          history:   [],
        }, { timeout: 120000 });

        const data    = response.data || {};
        const content = data.content || "";

        if (!content) {
          throw new Error("Réponse du serveur local sans contenu.");
        }

        const extracted = extractJsonValueFromText(content);
        const child = this.$refs.consignmentCreateRef;
        if (!child) {
          throw new Error("Formulaire dossier d’importation indisponible.");
        }

        const payload =
          extracted && typeof extracted === "object" ? extracted : {};

        const supplierInv =
          payload.supplier_invoice && typeof payload.supplier_invoice === "object"
            ? payload.supplier_invoice
            : null;
        const otherInvoices = Array.isArray(payload.other_invoices)
          ? payload.other_invoices
          : [];

        // Exchange rate (taux de change): prefer supplier invoice, else first other invoice.
        const inferredCurrency = this.normalizeCurrency(
          (supplierInv && supplierInv.currency) ||
            (otherInvoices[0] && otherInvoices[0].currency) ||
            "MAD"
        );
        const inferredExchangeRate = (() => {
          const r1 = supplierInv && supplierInv.exchange_rate;
          const r2 =
            otherInvoices &&
            otherInvoices[0] &&
            typeof otherInvoices[0] === "object" &&
            otherInvoices[0].exchange_rate;
          const n = this.parseExchangeRate(r1 != null ? r1 : r2);
          if (inferredCurrency === "MAD") return 1;
          return n;
        })();

        if (inferredCurrency !== "MAD" && !inferredExchangeRate) {
          throw new Error(
            `Taux de change introuvable ou invalide (devise=${inferredCurrency}). Assurez-vous que la facture indique le taux (ex: 1 USD = 10.25 MAD).`
          );
        }

        // Fill consignment exchange rate immediately (form field).
        if (inferredExchangeRate) {
          if (child.consignment) child.consignment.exchange_rate = inferredExchangeRate;
          if (child.selectedItem) child.selectedItem.exchange_rate = inferredExchangeRate;
        }

        // 1) Save costs for "other invoices" (even without supplier).
        if (typeof child.importExtractedCosts === "function") {
          await child.importExtractedCosts(otherInvoices, {
            invoiceFile: this.lastUploadedImportFile,
            skipExisting: true,
            exchange_rate: inferredExchangeRate,
          });
        }

        // 2) Save costs + items for supplier invoice (only if supplier info exists).
        const supplier_fullName = String(
          (supplierInv && supplierInv.supplier_fullName) || ""
        ).trim();
        const invoice_number = String(
          (supplierInv && supplierInv.invoice_number) || ""
        ).trim();
        const invoice_date = String(
          (supplierInv && supplierInv.invoice_date) || ""
        ).trim();
        const currency = this.normalizeCurrency(
          (supplierInv && supplierInv.currency) || inferredCurrency || "MAD"
        );
        const exchange_rate =
          currency === "MAD"
            ? 1
            : this.parseExchangeRate(
                (supplierInv && supplierInv.exchange_rate) || inferredExchangeRate
              ) || null;

        if (currency !== "MAD" && !exchange_rate) {
          throw new Error(
            `Taux de change introuvable ou invalide pour la facture fournisseur (devise=${currency}).`
          );
        }

        // Validate: supplier invoice total (doc currency) == sum(items totals in doc currency).
        if (supplierInv && supplier_fullName) {
          const invoiceTotal = parseFloat(supplierInv.total_ht);
          const itemsTotal = this.computeItemsTotalInDocCurrency(payload.items || []);
          if (
            isFinite(invoiceTotal) &&
            invoiceTotal > 0 &&
            itemsTotal > 0 &&
            !this.nearlyEqualTotals(invoiceTotal, itemsTotal)
          ) {
            throw new Error(
              `Erreur: Total facture (${currency} HT=${invoiceTotal}) ≠ Total lignes (${currency} HT≈${itemsTotal.toFixed(
                2
              )}). Vérifiez le document ou relancez l’analyse.`
            );
          }
        }

        if (supplier_fullName && typeof child.importExtractedCosts === "function") {
          await child.importExtractedCosts(
            [
              {
                total_ht: supplierInv && supplierInv.total_ht,
                total_ttc: supplierInv && supplierInv.total_ttc,
                total_tva: supplierInv && supplierInv.total_tva,
                tva: supplierInv && supplierInv.tva,
                remark: "Facture fournisseur",
                supplier_fullName,
                invoice_number,
                invoice_date,
                currency,
                exchange_rate,
              },
            ],
            {
              invoiceFile: this.lastUploadedImportFile,
              skipExisting: true,
            }
          );
        }

        if (supplier_fullName && typeof child.importExtractedLines === "function") {
          const groupedItems = this.groupSupplierItemsByFullName(payload.items || []);
          await child.importExtractedLines(groupedItems, {
            dumFile: this.lastUploadedImportFile,
            mergeDuplicates: false,
            skipExisting: true,
          });
        }

        await this.$store.dispatch("consignment/getAllCosts");
        await this.$store.dispatch("consignment/getAllItems");
      } catch (e) {
        this.fillDumError =
          e.message ||
          "Impossible d’analyser le PDF (réseau, CORS ou clé API).";
      } finally {
        this.fillDumRunning = false;
      }
    },
    async importLastResponseLinesToConsignment() {
      this.importFromResponseError = "";
      const extracted = extractJsonValueFromText(this.lastResponse);
      const payload =
        extracted && typeof extracted === "object" ? extracted : null;
      const lines = Array.isArray(extracted)
        ? extracted
        : payload && Array.isArray(payload.items)
          ? payload.items
          : [];
      if (!lines.length) {
        this.importFromResponseError =
          "Aucun tableau JSON détecté. Demandez à ChatPDF une réponse au format JSON uniquement.";
        return;
      }
      const child = this.$refs.consignmentCreateRef;
      if (!child || typeof child.importExtractedLines !== "function") {
        this.importFromResponseError =
          "Formulaire dossier d’importation indisponible.";
        return;
      }
      this.importFromResponseRunning = true;
      try {
        await child.importExtractedLines(lines, {
          dumFile: this.lastUploadedImportFile,
          mergeDuplicates: true,
          skipExisting: true,
        });
        await this.$store.dispatch("consignment/getAllItems");
      } catch (e) {
        this.importFromResponseError =
          e.message || "Impossible d’importer les lignes.";
      } finally {
        this.importFromResponseRunning = false;
      }
    },

    scrollToDumSection() {
      this.$nextTick(() => {
        const el = document.getElementById("bloc-facture-fournisseur-dum");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    clearError() {
      this.errorMessage = "";
    },

    formatApiError(data, res) {
      if (!data || typeof data !== "object") {
        return res.statusText || `HTTP ${res.status}`;
      }
      return (
        data.message ||
        data.error ||
        (Array.isArray(data.errors) && data.errors.join("; ")) ||
        `HTTP ${res.status}`
      );
    },

    triggerDumSectionFilePick() {
      if (this.$refs.dumSectionFileInput) {
        this.$refs.dumSectionFileInput.click();
      }
    },

    async onFileSelected(event) {
      await this.handleSourceFileSelected(event);
    },

    async onDumSectionFileSelected(event) {
      await this.handleSourceFileSelected(event);
    },

    isPdfFile(file) {
      if (!file || !file.name) return false;
      return (
        file.type === "application/pdf" ||
        String(file.name).toLowerCase().endsWith(".pdf")
      );
    },

    async handleSourceFileSelected(event) {
      this.clearError();
      this.fillDumError = "";
      this.lastResponse = "";
      const input = event && event.target;
      const file = input && input.files && input.files[0];
      if (!file) return;
      if (input) input.value = "";

      this.uploading = true;
      this.uploadedFileName = file.name;
      this.lastUploadedImportFile = file;
      this.fileBase64 = "";
      this.fileMimeType = file.type || "application/octet-stream";

      try {
        this.fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload  = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } catch (e) {
        this.uploadedFileName = "";
        this.fileBase64 = "";
        this.lastUploadedImportFile = null;
        this.errorMessage = "Impossible de lire le fichier.";
      } finally {
        this.uploading = false;
      }
    },

    resetFileInputOnly() {
      this.uploadedFileName = "";
      this.fileBase64 = "";
      this.fileMimeType = "";
      this.lastUploadedImportFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      if (this.$refs.dumSectionFileInput) this.$refs.dumSectionFileInput.value = "";
    },

    resetFile() {
      this.resetFileInputOnly();
      this.clearError();
      this.lastResponse = "";
    },


    // =================== LocalChat AI ===================
    lcClear() {
      this.lcMessages = [];
      this.lcError = "";
    },

    lcScrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.lcMessages;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    async lcFetchProvider() {
      try {
        const res = await fetch(`${LOCAL_CHAT_BASE}/v1/providers/active`, {
          headers: { Authorization: `Bearer ${this.lcToken || "lc_key"}` },
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          this.lcProviderLabel = data.name || data.provider || "";
        }
      } catch (_) {}
    },

    lcOnFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.lcFile = file;
      if (this.$refs.lcFileInput) this.$refs.lcFileInput.value = "";
    },

    buildLcHistory() {
      return this.lcMessages
        .filter((m) => !m.streaming)
        .map((m) => ({ role: m.role, content: m.content }));
    },

    lcExtractContent(data) {
      // Normalise response from both /api/ask and /v1/chat
      if (data && data.message && data.message.content != null) {
        return typeof data.message.content === "string"
          ? data.message.content
          : JSON.stringify(data.message.content, null, 2);
      }
      if (data && data.response && data.response.message && data.response.message.content != null) {
        return String(data.response.message.content);
      }
      if (data && data.content != null) {
        return typeof data.content === "string" ? data.content : JSON.stringify(data.content, null, 2);
      }
      return JSON.stringify(data, null, 2);
    },

    async sendLocalChat() {
      const text = this.lcInput.trim();
      const hasFile = !!this.lcFile;
      if (!text && !hasFile) return;
      if (this.lcStreaming) return;

      this.lcError = "";
      this.lcInput = "";

      const currentFile = this.lcFile;
      this.lcFile = null;

      // Add user message
      this.lcMessages.push({
        role: "user",
        content: text || `[Fichier: ${currentFile ? currentFile.name : ""}]`,
        fileName: currentFile ? currentFile.name : null,
      });
      this.lcScrollBottom();

      // Placeholder assistant message
      const assistantMsg = { role: "assistant", content: "", streaming: true };
      this.lcMessages.push(assistantMsg);
      this.lcScrollBottom();

      this.lcStreaming = true;

      try {
        // Build payload — file read as base64 so the backend can proxy to the local server
        const payload = {
          message: text || "Analyse ce fichier.",
          history: this.buildLcHistory().slice(0, -1),
        };

        if (hasFile) {
          // Read file as base64 data URL on the client
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload  = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(currentFile);
          });
          payload.file_data = dataUrl;
          payload.file_name = currentFile.name;
          payload.mime_type = currentFile.type || "application/octet-stream";
        }

        // Route through the Laravel backend — it handles SSE parsing and returns clean JSON
        const response = await this.$api.instance.post("chatbot/localchat", payload, {
          timeout: 120000,
        });

        const data = response.data || {};
        const content = this.lcExtractContent(data);

        if (data.provider) this.lcProviderLabel = data.name || data.provider;

        assistantMsg.content = content;
        this.$set(this.lcMessages, this.lcMessages.length - 1, { ...assistantMsg });
        this.lcScrollBottom();
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.error || e.response.data.message))
            ? (e.response.data.error || e.response.data.message)
            : e.message || "Impossible de contacter le serveur (vérifiez qu'il tourne sur le port 7860).";
        this.lcError = msg;
        if (!assistantMsg.content) {
          this.lcMessages.splice(this.lcMessages.length - 1, 1);
        }
      } finally {
        assistantMsg.streaming = false;
        this.$set(this.lcMessages, this.lcMessages.length - 1, { ...assistantMsg });
        this.lcStreaming = false;
      }
    },
    // =====================================================

    async sendQuestion() {
      if (!this.canSend || this.chatting) return;

      this.chatting = true;
      this.clearError();
      this.lastResponse = "";

      try {
        const response = await this.$api.instance.post("chatbot/localchat", {
          message:   this.question,
          file_data: this.fileBase64  || undefined,
          file_name: this.uploadedFileName || undefined,
          mime_type: this.fileMimeType || undefined,
          history:   [],
        }, { timeout: 120000 });

        const data = response.data || {};
        this.lastResponse = data.content || JSON.stringify(data, null, 2);
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.error || e.response.data.message))
            ? (e.response.data.error || e.response.data.message)
            : e.message || "Erreur réseau : vérifiez que le serveur tourne sur http://127.0.0.1:7860.";
        this.errorMessage = msg;
      } finally {
        this.chatting = false;
      }
    },
  },
};
</script>

<style scoped>
.chatpdf-test {
  min-height: 60vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 40%);
}

.chatpdf-test__inner,
.chatpdf-test__section-wide {
  max-width: none;
  width: 100%;
}

.chatpdf-test__pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 0.95rem;
  line-height: 1.5;
}

.chatpdf-test__answer {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.chatpdf-test__answer-head {
  background: #f8f9fa;
}

/* -------- LocalChat AI -------- */
.lc-card {
  border: 1px solid rgba(99, 102, 241, 0.25) !important;
}

.lc-card__header {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #fff;
  border-radius: 0.375rem 0.375rem 0 0;
}

.lc-messages {
  max-height: 420px;
  overflow-y: auto;
  background: #f8fafc;
  scroll-behavior: smooth;
}

.lc-msg {
  max-width: 100%;
}

.lc-msg--user {
  flex-direction: row-reverse;
}

.lc-msg--user .lc-msg__avatar {
  margin-right: 0;
  margin-left: 8px;
}

.lc-msg__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.lc-msg--user .lc-msg__avatar {
  background: #6366f1;
  color: #fff;
}

.lc-msg--assistant .lc-msg__avatar {
  background: #e0e7ff;
  color: #4f46e5;
}

.lc-msg__bubble {
  max-width: 80%;
  padding: 0.55rem 0.85rem;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}

.lc-msg--user .lc-msg__bubble {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
}

.lc-msg__text {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 0.9rem;
  line-height: 1.5;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  color: inherit;
}

/* Typing indicator */
.lc-typing {
  display: flex;
  gap: 4px;
  align-items: center;
}

.lc-typing span {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4f46e5;
  animation: lc-bounce 1.2s infinite ease-in-out;
}

.lc-typing span:nth-child(2) { animation-delay: 0.2s; }
.lc-typing span:nth-child(3) { animation-delay: 0.4s; }

@keyframes lc-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: .5; }
  40%            { transform: scale(1);   opacity: 1;  }
}

.lc-input-area {
  background: #fff;
}

.lc-textarea {
  resize: none;
  border-radius: 0.75rem;
  font-size: 0.9rem;
}

.lc-send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #6366f1;
  border-color: #6366f1;
}

.lc-send-btn:hover:not(:disabled) {
  background: #4f46e5;
  border-color: #4f46e5;
}

.lc-attach-btn {
  height: 44px;
  width: 38px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.lc-file-badge {
  font-size: 0.75rem;
  color: #4f46e5;
  opacity: 0.85;
}

.lc-remove-file {
  font-size: 1rem;
  line-height: 1;
  text-decoration: none;
}
</style>
